import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Users, MapPin, ArrowRight, Menu, X, ChevronDown, Instagram, Zap } from "lucide-react";
import "./App.css";

const ICON_URL = "https://customer-assets.emergentagent.com/job_uva-huddle/artifacts/p3xvp3oh_transparent-logo1.png";
const LOGO_URL  = "https://customer-assets.emergentagent.com/job_uva-huddle/artifacts/td57b26x_transparent-logo.png";
const ROTUNDA_IMG = "https://customer-assets.emergentagent.com/job_uva-huddle/artifacts/7r4ajwyk_Inline-07-NA-Fall-Grounds-MR.jpg";
const LAWN_IMG  = "https://customer-assets.emergentagent.com/job_uva-huddle/artifacts/qj321cz6_HP-Life-At-UVA-Students-playing-volleyball-on-sunny-Lawn-day-2400x1200%20%281%29.jpg";
const CAMPUS_IMG = "https://customer-assets.emergentagent.com/job_uva-huddle/artifacts/rfiq1cqu_bb6fd6dc-df45-4115-b84a-2f10fd5f0daf.jpg";

const NAVY   = "#232D4B";
const ORANGE = "#E57200";
const BG     = "#F6F6F2";

const SECTIONS = ["how-it-works", "showcase", "about", "survey"];

function useScrollInView() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return { ref, isInView };
}

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }
  }),
};

// ─── NAVBAR ──────────────────────────────────────────────────────────────────
function Navbar() {
  const [open, setOpen]       = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track active section via IntersectionObserver
  useEffect(() => {
    if (!isHome) return;
    const observers = [];
    SECTIONS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [isHome]);

  const linkColor = scrolled ? NAVY : "#FFFFFF";

  const navLinks = [
    { label: "How It Works", href: "#how-it-works", id: "how-it-works", testid: "nav-how-it-works" },
    { label: "About",        href: "#about",        id: "about",        testid: "nav-about"        },
    { label: "Take the Survey", href: "#survey",    id: "survey",       testid: "nav-survey-link"  },
  ];

  const isActive = (id) => isHome && activeSection === id;

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-12 py-3"
      style={{
        background:    scrolled ? "rgba(246,246,242,0.96)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom:  scrolled ? `1px solid ${NAVY}18` : "none",
        transition:    "background 0.35s, border 0.35s",
      }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Icon — always visible, home = scroll top, elsewhere = navigate home */}
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center"
          onClick={(e) => {
            if (isHome) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden"
            style={{ background: "#FFFFFF", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
          >
            <img src={ICON_URL} alt="Atloria Live" className="w-10 h-10 object-contain" />
          </div>
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.testid}
              href={isHome ? l.href : `/${l.href}`}
              data-testid={l.testid}
              className="text-sm font-semibold tracking-wide transition-all pb-0.5 relative"
              style={{
                color: isActive(l.id) ? ORANGE : linkColor,
                fontFamily: "Plus Jakarta Sans, sans-serif",
                borderBottom: isActive(l.id) ? `2px solid ${ORANGE}` : "2px solid transparent",
                transition: "color 0.25s, border-color 0.25s",
              }}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/terms"
            data-testid="nav-terms-link"
            className="text-sm font-semibold tracking-wide transition-colors hover:text-[#E57200]"
            style={{ color: linkColor, fontFamily: "Plus Jakarta Sans, sans-serif" }}
          >
            Terms
          </Link>
          <a
            href="https://atloria.live"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="px-5 py-2.5 text-sm font-bold rounded-full text-white transition-all hover:-translate-y-0.5"
            style={{
              background: ORANGE,
              fontFamily: "Plus Jakarta Sans, sans-serif",
              boxShadow: `3px 3px 0px ${NAVY}`,
            }}
          >
            Join an Adventure
          </a>
        </nav>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          className="md:hidden p-2 rounded-lg"
          onClick={() => setOpen(!open)}
          style={{ color: linkColor, background: scrolled ? "transparent" : "#FFFFFF20" }}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden absolute top-full left-0 right-0 border-t-2 py-6 px-6 flex flex-col gap-5"
          style={{ background: BG, borderColor: NAVY }}
        >
          {navLinks.map((l, i) => (
            <a key={i} href={l.href} onClick={() => setOpen(false)}
              className="text-base font-semibold"
              style={{ color: isActive(l.id) ? ORANGE : NAVY, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              {l.label}
            </a>
          ))}
          <Link to="/terms" onClick={() => setOpen(false)}
            data-testid="mobile-terms-link"
            className="text-base font-semibold"
            style={{ color: NAVY, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            Terms
          </Link>
          <a href="https://atloria.live" target="_blank" rel="noopener noreferrer"
            data-testid="mobile-cta-button"
            className="w-full text-center px-6 py-3 font-bold rounded-full text-white"
            style={{ background: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            Join an Adventure
          </a>
        </motion.div>
      )}
    </header>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: NAVY }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-[0.15]"
          style={{ background: ORANGE }}
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-48 -left-24 w-[500px] h-[500px] rounded-full opacity-[0.08]"
          style={{ background: ORANGE }}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-32 pb-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.h1
              variants={fadeUp} initial="hidden" animate="visible" custom={0}
              className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-6"
              style={{ color: "#FFFFFF", fontFamily: "Outfit, sans-serif" }}
            >
              At UVA
              <br />
              Become the <span style={{ color: ORANGE }}>Lore.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp} initial="hidden" animate="visible" custom={1}
              className="text-base sm:text-lg leading-relaxed mb-10 max-w-md"
              style={{ color: "#C8CFDD", fontFamily: "Plus Jakarta Sans, sans-serif" }}
            >
              Get matched with other UVA students nearby, and go on an adventure together!
            </motion.p>

            <motion.div
              variants={fadeUp} initial="hidden" animate="visible" custom={2}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="https://atloria.live"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-button"
                className="inline-flex items-center gap-2 px-8 py-4 font-bold text-base rounded-full transition-all hover:-translate-y-1"
                style={{
                  background: ORANGE, color: "#FFFFFF",
                  fontFamily: "Plus Jakarta Sans, sans-serif",
                  boxShadow: `4px 4px 0px rgba(229,114,0,0.4)`,
                }}
              >
                Join an Adventure
                <ArrowRight size={18} />
              </a>
              <a
                href="#how-it-works"
                data-testid="hero-learn-more"
                className="inline-flex items-center gap-2 px-8 py-4 font-bold text-base rounded-full border-2 transition-all hover:-translate-y-1"
                style={{ borderColor: "#FFFFFF30", color: "#FFFFFF", fontFamily: "Plus Jakarta Sans, sans-serif" }}
              >
                How It Works
                <ChevronDown size={18} />
              </a>
            </motion.div>
          </div>

          {/* Logo card */}
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="hidden lg:flex justify-center items-center"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-3xl p-10 flex items-center justify-center"
              style={{
                background: "#FFFFFF",
                boxShadow: `0 32px 80px rgba(0,0,0,0.35), 8px 8px 0px ${ORANGE}`,
                border: `2px solid ${ORANGE}30`,
              }}
            >
              <img src={LOGO_URL} alt="Atloria Live" className="w-64 h-64 object-contain" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── MARQUEE ──────────────────────────────────────────────────────────────────
function LocationMarquee() {
  const items = ["THE ROTUNDA", "1515", "NAU HALL", "THE CORNER", "THE LAWN", "THE CHAPEL"];
  return (
    <div data-testid="location-marquee" className="py-5 border-y-2 overflow-hidden"
      style={{ background: ORANGE, borderColor: NAVY }}>
      <Marquee speed={55} gradient={false}>
        {items.map((item, i) => (
          <span key={i} className="text-sm font-black tracking-[0.25em] uppercase mx-10"
            style={{ color: NAVY, fontFamily: "Outfit, sans-serif" }}>
            {item}<span className="mx-8" style={{ color: `${NAVY}40` }}>•</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

// ─── HOW IT WORKS ─────────────────────────────────────────────────────────────
function HowItWorks() {
  const { ref, isInView } = useScrollInView();
  const steps = [
    { num: "01", title: "Join the Queue", desc: "Hit \"Go Live\" to get in the next match.", icon: <Zap size={26} />, label: "GO LIVE" },
    { num: "02", title: "Get Matched",    desc: "We group you with other UVA students nearby.", icon: <Users size={26} />, label: "MATCH NEAR YOU" },
    { num: "03", title: "Meet Up",        desc: "Navigate to your assigned on-Grounds location and let the adventure begin.", icon: <MapPin size={26} />, label: "GROUP IS LIVE" },
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 lg:py-32" style={{ background: BG }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={ref} className="text-center mb-16">
          <motion.span variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"}
            className="inline-block text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full border-2 mb-5"
            style={{ color: ORANGE, borderColor: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            The Flow
          </motion.span>
          <motion.h2 variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={1}
            className="text-4xl sm:text-5xl font-black tracking-tight"
            style={{ color: NAVY, fontFamily: "Outfit, sans-serif" }}>
            How It Works
          </motion.h2>
          <motion.p variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={2}
            className="mt-4 text-base sm:text-lg max-w-lg mx-auto"
            style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            No plans. All Spontaneous. Just show up.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((step, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={i + 3}
              data-testid={`how-it-works-step-${i + 1}`}
              className="relative p-8 rounded-2xl border-2 hover:-translate-y-2 transition-transform cursor-default"
              style={{ background: "#FFFFFF", borderColor: NAVY, boxShadow: `4px 4px 0px ${NAVY}` }}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 border-2"
                style={{ background: `${ORANGE}15`, borderColor: ORANGE, color: ORANGE }}>
                {step.icon}
              </div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase mb-2"
                style={{ color: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{step.label}</div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: NAVY, fontFamily: "Outfit, sans-serif" }}>{step.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>{step.desc}</p>
              <div className="absolute top-6 right-6 text-5xl font-black opacity-10"
                style={{ color: NAVY, fontFamily: "Outfit, sans-serif" }}>{step.num}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SHOWCASE ─────────────────────────────────────────────────────────────────
function Showcase() {
  const { ref, isInView } = useScrollInView();
  const cards = [
    { img: ROTUNDA_IMG,  label: "UVA",             caption: "Your adventure starts here." },
    { img: LAWN_IMG,     label: "The Lawn",         caption: "Where Grounds comes alive." },
    { img: CAMPUS_IMG,   label: "Explore Grounds",  caption: "Every free period is a new journey." },
  ];

  return (
    <section id="showcase" data-testid="showcase-section" className="py-24 lg:py-32" style={{ background: NAVY }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={ref} className="text-center mb-14">
          <motion.span variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"}
            className="inline-block text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full border-2 mb-5"
            style={{ color: ORANGE, borderColor: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            On Grounds
          </motion.span>
          <motion.h2 variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={1}
            className="text-4xl sm:text-5xl font-black tracking-tight text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}>
            Your Adventure Starts Here
          </motion.h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {cards.map((item, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={i + 2}
              data-testid={`showcase-card-${i}`}
              className="relative rounded-2xl overflow-hidden border-2 group"
              style={{ borderColor: `${ORANGE}40`, boxShadow: `4px 4px 0px ${ORANGE}30` }}>
              <div className="relative h-72 overflow-hidden">
                <img src={item.img} alt={item.label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0"
                  style={{ background: `linear-gradient(to top, ${NAVY}E0 0%, transparent 55%)` }} />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className="text-xs font-bold tracking-widest uppercase px-2 py-1 rounded-full mb-2 inline-block"
                  style={{ background: ORANGE, color: "#FFFFFF", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                  {item.label}
                </span>
                <p className="text-white font-semibold text-sm mt-1"
                  style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>{item.caption}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── ABOUT ────────────────────────────────────────────────────────────────────
function About() {
  const { ref, isInView } = useScrollInView();

  return (
    <section id="about" data-testid="about-section" className="py-24 lg:py-32 overflow-hidden" style={{ background: BG }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={ref} className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image — UVA Lawn volleyball photo */}
          <motion.div variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"}
            className="relative order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden border-2"
              style={{ borderColor: NAVY, boxShadow: `8px 8px 0px ${NAVY}` }}>
              <img src={LAWN_IMG} alt="UVA students on the Lawn"
                data-testid="about-image"
                className="w-full h-[460px] object-cover object-top" />
              <div className="absolute inset-0 rounded-2xl"
                style={{ background: `linear-gradient(to top, ${NAVY}40, transparent 60%)` }} />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.7 }}
              className="absolute -bottom-4 -right-4 px-5 py-3 rounded-xl border-2"
              style={{ background: ORANGE, borderColor: NAVY, boxShadow: `3px 3px 0px ${NAVY}` }}>
              <div className="text-xs font-bold text-white/80 uppercase tracking-widest"
                style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>UVA Only</div>
              <div className="font-black text-white" style={{ fontFamily: "Outfit, sans-serif" }}>@virginia.edu required</div>
            </motion.div>
          </motion.div>

          {/* Text */}
          <div className="order-1 lg:order-2">
            <motion.span variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"}
              className="inline-block text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full border-2 mb-5"
              style={{ color: ORANGE, borderColor: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              About Atloria Live
            </motion.span>
            <motion.h2 variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={1}
              className="text-4xl sm:text-5xl font-black tracking-tight mb-6"
              style={{ color: NAVY, fontFamily: "Outfit, sans-serif" }}>
              Campus as a<br /><span style={{ color: ORANGE }}>Living Adventure</span>
            </motion.h2>
            <motion.p variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={2}
              className="text-base sm:text-lg leading-relaxed mb-4"
              style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Atloria turns campus into a living adventure. We simply group you with other UVA students who are free in the moment for a spontaneous hangout.
            </motion.p>
            <motion.p variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={3}
              className="text-base leading-relaxed mb-8"
              style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Bring your unique personality to the journey, explore Grounds, and become a part of an adventure. No plans needed — just show up and see where it takes you.
            </motion.p>
            <motion.a variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"} custom={4}
              href="https://atloria.live" target="_blank" rel="noopener noreferrer"
              data-testid="about-cta-button"
              className="inline-flex items-center gap-2 px-8 py-4 font-bold text-base rounded-full transition-all hover:-translate-y-1"
              style={{ background: ORANGE, color: "#FFFFFF", fontFamily: "Plus Jakarta Sans, sans-serif", boxShadow: `4px 4px 0px ${NAVY}` }}>
              Join an Adventure
              <ArrowRight size={18} />
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SURVEY MODAL ─────────────────────────────────────────────────────────────
const SURVEY_QUESTIONS = [
  {
    id: "email",
    q: "What's your email?",
    type: "email",
  },
  {
    id: "year",
    q: "What year are you?",
    type: "single",
    options: ["1st Year", "2nd Year", "3rd Year", "4th Year", "Transfer"],
  },
  {
    id: "social_circle",
    q: "What does your current social circle mostly revolve around?",
    type: "multi",
    options: ["Dorms", "Clubs", "Greek Life", "My Major", "Sports", "Work / Job", "Online friends"],
  },
  {
    id: "free_hour",
    q: "When you have free time, what is it that you are doing most of the time?",
    type: "single",
    options: [
      "Brainrotting on my phone",
      "Studying alone",
      "Traveling on Grounds",
      "Hanging out with a group I already have plans with",
    ],
  },
  {
    id: "chilling_alone",
    q: "Think about the last week. Did you ever end up chilling alone just because figuring out who was around felt like too much effort?",
    type: "single",
    options: ["Yes, often", "Once or twice", "No — I always make plans"],
  },
  {
    id: "hangout_method",
    q: "When you want to hang out with people, what do you usually do?",
    type: "single",
    options: [
      "I usually just end up staying in — it's too hard to plan",
      "Send a 'Who's free?' text to the dead group chat",
      "Checking emails for events on Grounds",
      "Waiting for someone I know to tell me to do something",
    ],
  },
  {
    id: "spontaneous_spot",
    q: "Where are you usually closest to on Grounds?",
    type: "multi",
    options: ["O-Hill", "Newcomb", "Runk", "The Rotunda", "The Corner", "Somewhere else"],
  },
  {
    id: "barrier",
    q: "What's the biggest barrier to spontaneous hangouts actually happening?",
    type: "single",
    options: [
      "Group chats are too slow / cluttered",
      "Don't want to be the one to initiate",
      "Don't know who's actually free right now",
      "Hard to decide on an activity quickly",
    ],
  },
  {
    id: "remove_one_thing",
    q: "If you could instantly remove one annoying thing about making plans on Grounds, what would it be?",
    type: "text",
  },
];

function SurveyModal({ onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const q = SURVEY_QUESTIONS[step];
  const total = SURVEY_QUESTIONS.length;
  const progress = ((step) / total) * 100;

  function select(optionOrText) {
    if (q.type === "multi") {
      const prev = answers[q.id] || [];
      const updated = prev.includes(optionOrText)
        ? prev.filter((x) => x !== optionOrText)
        : [...prev, optionOrText];
      setAnswers({ ...answers, [q.id]: updated });
    } else {
      setAnswers({ ...answers, [q.id]: optionOrText });
    }
  }

  function isSelected(option) {
    if (q.type === "multi") return (answers[q.id] || []).includes(option);
    return answers[q.id] === option;
  }

  function canAdvance() {
    if (q.type === "text") return (answers[q.id] || "").trim().length > 0;
    if (q.type === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers[q.id] || "");
    if (q.type === "multi") return (answers[q.id] || []).length > 0;
    return !!answers[q.id];
  }

  function next() {
    if (step < total - 1) setStep(step + 1);
    else handleSubmit();
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/survey", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(answers),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setSubmitting(false);
  }

  const LB = "#DBEAFE"; // light blue
  const LB2 = "#93C5FD";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(35,45,75,0.82)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{
          background: "#FFFFFF",
          borderRadius: "24px",
          width: "100%",
          maxWidth: "520px",
          maxHeight: "92vh",
          overflowY: "auto",
          border: `2.5px solid ${ORANGE}`,
          boxShadow: `8px 8px 0px ${ORANGE}`,
          position: "relative",
        }}
      >
        {/* Header gradient */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #1e3a6e 50%, #1a5276 100%)`,
          borderRadius: "21px 21px 0 0",
          padding: "28px 28px 24px",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* decorative circles */}
          <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120, borderRadius:"50%", background:"rgba(229,114,0,0.15)" }} />
          <div style={{ position:"absolute", bottom:-20, left:-20, width:80, height:80, borderRadius:"50%", background:"rgba(147,197,253,0.12)" }} />

          {/* close */}
          <button onClick={onClose} style={{
            position:"absolute", top:16, right:16, background:"rgba(255,255,255,0.15)",
            border:"none", borderRadius:"50%", width:32, height:32, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", color:"#fff",
          }}>
            <X size={16} />
          </button>

          {/* Logo */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", marginBottom:14 }}>
            <img src={ICON_URL} alt="Atloria" style={{ height:40, objectFit:"contain" }} />
          </div>

          <p style={{
            textAlign:"center", fontFamily:"Plus Jakarta Sans, sans-serif",
            fontSize:11, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase",
            color: LB2, marginBottom:6,
          }}>45 Seconds · Shape Atloria</p>

          <h2 style={{
            textAlign:"center", fontFamily:"Outfit, sans-serif",
            fontWeight:800, fontSize:"clamp(18px,4vw,22px)", color:"#fff",
            lineHeight:1.2, margin:0,
          }}>
            {submitted ? "Thanks, Hoo! 🎉" : "Tell us about your social life on Grounds"}
          </h2>

          {!submitted && (
            <div style={{ marginTop:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                <span style={{ fontFamily:"Plus Jakarta Sans, sans-serif", fontSize:11, color:LB2, fontWeight:600 }}>
                  Question {step + 1} of {total}
                </span>
                <span style={{ fontFamily:"Plus Jakarta Sans, sans-serif", fontSize:11, color:LB2, fontWeight:600 }}>
                  {Math.round(((step) / total) * 100)}%
                </span>
              </div>
              <div style={{ height:4, background:"rgba(255,255,255,0.15)", borderRadius:100, overflow:"hidden" }}>
                <motion.div
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4 }}
                  style={{ height:"100%", background:`linear-gradient(90deg, ${ORANGE}, #FFA040)`, borderRadius:100 }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding:"28px 28px 32px" }}>
          {submitted ? (
            <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} style={{ textAlign:"center", padding:"16px 0" }}>
              <div style={{
                width:64, height:64, borderRadius:"50%", margin:"0 auto 20px",
                background:`linear-gradient(135deg, ${LB} 0%, #BFDBFE 100%)`,
                display:"flex", alignItems:"center", justifyContent:"center",
                border:`2px solid ${LB2}`,
              }}>
                <Zap size={28} color={NAVY} />
              </div>
              <h3 style={{ fontFamily:"Outfit, sans-serif", fontWeight:800, fontSize:20, color:NAVY, marginBottom:10 }}>
                You're shaping Atloria.
              </h3>
              <p style={{ fontFamily:"Plus Jakarta Sans, sans-serif", fontSize:14, color:"#556075", lineHeight:1.6, marginBottom:24 }}>
                Your answers help us build the spontaneous hangout layer UVA actually needs. We'll be in touch soon.
              </p>
              <button onClick={onClose} style={{
                background:`linear-gradient(135deg, ${NAVY} 0%, #1e3a6e 100%)`,
                border:"none", borderRadius:50, padding:"14px 36px", color:"#fff",
                fontFamily:"Outfit, sans-serif", fontWeight:700, fontSize:14,
                cursor:"pointer", boxShadow:`4px 4px 0 ${ORANGE}`,
              }}>
                Close
              </button>
            </motion.div>
          ) : (
            <motion.div key={step} initial={{ opacity:0, x:24 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.25 }}>
              {/* Question */}
              <div style={{
                background:`linear-gradient(135deg, ${LB}60 0%, #EFF6FF 100%)`,
                border:`1.5px solid ${LB2}`,
                borderRadius:16, padding:"16px 18px", marginBottom:20,
              }}>
                <p style={{
                  fontFamily:"Plus Jakarta Sans, sans-serif", fontSize:10, fontWeight:700,
                  letterSpacing:"0.18em", textTransform:"uppercase", color:ORANGE, marginBottom:6,
                }}>
                  {q.type === "multi" ? "Select all that apply" : q.type === "text" ? "Open-ended" : "Choose one"}
                </p>
                <p style={{ fontFamily:"Outfit, sans-serif", fontWeight:700, fontSize:16, color:NAVY, lineHeight:1.4, margin:0 }}>
                  {q.q}
                </p>
              </div>

              {/* Options */}
              {q.type === "email" ? (
                <input
                  type="email"
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                  placeholder="your@virginia.edu"
                  style={{
                    width:"100%", borderRadius:14, border:`2px solid ${LB2}`,
                    padding:"14px 16px", fontFamily:"Plus Jakarta Sans, sans-serif",
                    fontSize:15, color:NAVY, outline:"none",
                    background:`linear-gradient(135deg, ${LB}30 0%, #F8FAFF 100%)`,
                    boxSizing:"border-box", transition:"border-color 0.2s",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = ORANGE; }}
                  onBlur={(e) => { e.target.style.borderColor = LB2; }}
                />
              ) : q.type !== "text" ? (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {q.options.map((opt) => {
                    const sel = isSelected(opt);
                    return (
                      <button key={opt} onClick={() => select(opt)} style={{
                        background: sel ? `linear-gradient(135deg, ${NAVY} 0%, #1e3a6e 100%)` : `linear-gradient(135deg, ${LB}40 0%, #F8FAFF 100%)`,
                        border: sel ? `2px solid ${ORANGE}` : `2px solid ${LB2}`,
                        borderRadius:12, padding:"13px 16px", textAlign:"left",
                        cursor:"pointer", transition:"all 0.18s",
                        fontFamily:"Plus Jakarta Sans, sans-serif", fontSize:14, fontWeight:600,
                        color: sel ? "#fff" : NAVY,
                        boxShadow: sel ? `3px 3px 0 ${ORANGE}` : "none",
                        display:"flex", alignItems:"center", gap:10,
                      }}>
                        <span style={{
                          width:20, height:20, borderRadius: q.type === "multi" ? 6 : "50%",
                          border: sel ? `2px solid ${ORANGE}` : `2px solid ${LB2}`,
                          background: sel ? ORANGE : "transparent",
                          flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
                        }}>
                          {sel && <span style={{ width:8, height:8, borderRadius:"50%", background:"#fff", display:"block" }} />}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <textarea
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                  placeholder="Type your answer here..."
                  rows={4}
                  style={{
                    width:"100%", borderRadius:14, border:`2px solid ${LB2}`,
                    padding:"14px 16px", fontFamily:"Plus Jakarta Sans, sans-serif",
                    fontSize:14, color:NAVY, resize:"none", outline:"none",
                    background:`linear-gradient(135deg, ${LB}30 0%, #F8FAFF 100%)`,
                    boxSizing:"border-box", lineHeight:1.6,
                    transition:"border-color 0.2s",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = ORANGE; }}
                  onBlur={(e) => { e.target.style.borderColor = LB2; }}
                />
              )}

              {error && (
                <p style={{ color:"#dc2626", fontFamily:"Plus Jakarta Sans, sans-serif", fontSize:13, marginTop:10 }}>{error}</p>
              )}

              {/* Navigation */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:24 }}>
                {step > 0 ? (
                  <button onClick={() => setStep(step - 1)} style={{
                    background:"transparent", border:`2px solid ${LB2}`, borderRadius:50,
                    padding:"10px 22px", fontFamily:"Plus Jakarta Sans, sans-serif",
                    fontSize:13, fontWeight:700, color:NAVY, cursor:"pointer",
                  }}>
                    ← Back
                  </button>
                ) : <div />}

                <button
                  onClick={next}
                  disabled={!canAdvance() || submitting}
                  style={{
                    background: canAdvance() ? `linear-gradient(135deg, ${NAVY} 0%, #1e3a6e 100%)` : "#CBD5E1",
                    border:"none", borderRadius:50, padding:"13px 32px",
                    fontFamily:"Outfit, sans-serif", fontSize:14, fontWeight:700,
                    color:"#fff", cursor: canAdvance() ? "pointer" : "not-allowed",
                    boxShadow: canAdvance() ? `4px 4px 0 ${ORANGE}` : "none",
                    transition:"all 0.2s", display:"flex", alignItems:"center", gap:8,
                  }}
                >
                  {submitting ? "Submitting..." : step === total - 1 ? "Submit" : "Next →"}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── SURVEY CTA ───────────────────────────────────────────────────────────────
function SurveyCTA({ onOpenSurvey }) {
  const { ref, isInView } = useScrollInView();
  return (
    <section id="survey" data-testid="survey-section" className="py-20" style={{ background: NAVY }}>
      <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center" ref={ref}>
        <motion.div variants={fadeUp} initial="hidden" animate={isInView ? "visible" : "hidden"}
          className="p-10 rounded-2xl border-2"
          style={{ background: "#FFFFFF", borderColor: ORANGE, boxShadow: `6px 6px 0px ${ORANGE}` }}>
          <span className="inline-block mb-5 text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full border-2"
            style={{ color: ORANGE, borderColor: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            45 Seconds of Your Time
          </span>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4"
            style={{ color: NAVY, fontFamily: "Outfit, sans-serif" }}>Shape What's Coming</h2>
          <p className="text-base leading-relaxed mb-8"
            style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            We're building Atloria Live for Hoos like you. Your feedback directly shapes how Grounds comes alive.
          </p>
          <button onClick={onOpenSurvey} data-testid="survey-cta-button"
            className="inline-flex items-center gap-2 px-8 py-4 font-bold text-base rounded-full transition-all hover:-translate-y-1"
            style={{ background: NAVY, color: "#FFFFFF", fontFamily: "Plus Jakarta Sans, sans-serif", boxShadow: `4px 4px 0px ${ORANGE}`, border:"none", cursor:"pointer" }}>
            Take the Survey
            <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer data-testid="footer" className="pt-16 pb-8"
      style={{ background: NAVY, borderTop: `2px solid ${ORANGE}25` }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-3 gap-10 pb-12 border-b-2" style={{ borderColor: "#FFFFFF12" }}>
          <div>
            <div className="inline-block rounded-2xl p-4 mb-4" style={{ background: "#FFFFFF" }}>
              <img src={LOGO_URL} alt="Atloria Live" className="h-20 w-auto" />
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "#8A9BB5", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Grounds is Live.<br />Turn your free period into a real adventure.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-bold tracking-[0.2em] uppercase mb-5"
              style={{ color: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Navigate</h4>
            <div className="flex flex-col gap-3">
              {[
                { label: "How It Works",    href: "#how-it-works", testid: "footer-how-it-works" },
                { label: "About",           href: "#about",         testid: "footer-about" },
                { label: "Take the Survey", href: "#survey",        testid: "footer-survey-link" },
              ].map((link) => (
                <a key={link.testid} href={link.href} data-testid={link.testid}
                  className="text-sm hover:text-[#E57200] transition-colors"
                  style={{ color: "#8A9BB5", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                  {link.label}
                </a>
              ))}
              <Link to="/terms" data-testid="footer-terms-link"
                className="text-sm hover:text-[#E57200] transition-colors"
                style={{ color: "#8A9BB5", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                Terms of Service
              </Link>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-bold tracking-[0.2em] uppercase mb-5"
              style={{ color: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Get Involved</h4>
            <div className="flex flex-col gap-4">
              <a href="https://atloria.live" target="_blank" rel="noopener noreferrer"
                data-testid="footer-cta-button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 font-bold text-sm rounded-full transition-all hover:-translate-y-0.5"
                style={{ background: ORANGE, color: "#FFFFFF", fontFamily: "Plus Jakarta Sans, sans-serif", boxShadow: `3px 3px 0px ${ORANGE}50` }}>
                Join an Adventure <ArrowRight size={16} />
              </a>
              <a href="https://www.instagram.com/atloria.live" target="_blank" rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 font-bold text-sm rounded-full border-2 transition-all hover:-translate-y-0.5"
                style={{ borderColor: "#FFFFFF25", color: "#FFFFFF", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                <Instagram size={16} /> @atloria.live
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            © 2026 Atloria Live. Built for UVA Hoos.
          </p>
          <p className="text-xs" style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            @virginia.edu accounts only
          </p>
        </div>
      </div>
    </footer>
  );
}

// ─── MAIN LANDING PAGE ────────────────────────────────────────────────────────
function LandingPage({ autoOpenSurvey = false }) {
  const [surveyOpen, setSurveyOpen] = useState(autoOpenSurvey);

  useEffect(() => {
    if (autoOpenSurvey) {
      const el = document.getElementById("survey");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [autoOpenSurvey]);

  return (
    <>
      <Hero />
      <LocationMarquee />
      <HowItWorks />
      <Showcase />
      <About />
      <SurveyCTA onOpenSurvey={() => setSurveyOpen(true)} />
      <Footer />
      {surveyOpen && <SurveyModal onClose={() => setSurveyOpen(false)} />}
    </>
  );
}

// ─── TERMS PAGE ───────────────────────────────────────────────────────────────
function TermsPage() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const sections = [
    {
      num: "1",
      title: "Eligibility & Age Restriction",
      body: "The Service is strictly limited to users who are 18 years of age or older. By registering, you warrant that you are at least 18 years old. Accounts found to belong to minors will be terminated immediately and data deleted.",
    },
    {
      num: "2",
      title: "Assumption of Risk & Physical Safety",
      body: null,
      subsections: [
        {
          title: "Assumption of Risk",
          body: "Atloria Live connects humans for real-world interactions. We do not conduct criminal background checks or identity verification on our users. You acknowledge that meeting strangers and attending user-generated events carries inherent risks, including bodily injury, emotional distress, or theft. You agree that your participation in any activity is at your sole risk.",
        },
        {
          title: "Release of Liability",
          body: "ATLORIA LIVE HEREBY DISCLAIMS ALL LIABILITY FOR THE CONDUCT OF ANY USER OR THE OCCURRENCE OF ANY INCIDENT AT AN OFFLINE ACTIVITY. YOU HEREBY RELEASE THE COMPANY FROM ALL CLAIMS ARISING OUT OF YOUR INTERACTIONS WITH OTHER USERS.",
          highlight: true,
        },
        {
          title: "Safety Guidelines",
          body: null,
          list: [
            { label: "Meet in Public", detail: "Always choose a busy, public location for meetings." },
            { label: "Tell a Friend", detail: "Inform a trusted person of your plans and location." },
            { label: "Transportation", detail: "Arrange your own transportation to and from events." },
          ],
        },
      ],
    },
    {
      num: "3",
      title: "User Conduct",
      body: "You agree not to use the Service to solicit prostitution, sell drugs, or conduct illegal commerce; harass, stalk, defame, or threaten other users; post content containing nudity, graphic violence, or hate speech; or spam users or use the platform for unauthorized commercial advertising. We reserve the right to suspend or ban any account without refund or notice for violating these rules.",
    },
    {
      num: "4",
      title: "User Content & Licensing",
      body: "You retain ownership of the photos and event details you post. However, by posting, you grant Atloria Live a worldwide, royalty-free license to display, distribute, and use your content to operate and improve the Service.",
    },
    {
      num: "5",
      title: "Location Services",
      body: "The core functionality of the App requires precise geolocation. By using the App, you consent to the tracking of your location to display the feed and calculate distances to events.",
    },
    {
      num: "6",
      title: "Governing Law",
      body: "These Terms shall be governed by the laws of the jurisdiction in which the Company is incorporated, without regard to conflict of law principles.",
    },
    {
      num: "7",
      title: "Dispute Resolution",
      body: "Any dispute arising out of these Terms shall be resolved by binding arbitration. You waive your right to a trial by jury or to participate in a class action lawsuit.",
    },
  ];

  return (
    <div style={{ background: BG, minHeight: "100vh" }}>
      {/* Header */}
      <div className="pt-24 pb-16 px-6 lg:px-12" style={{ background: NAVY }}>
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            data-testid="terms-back-button"
            className="inline-flex items-center gap-2 text-sm font-semibold mb-5 transition-colors hover:text-[#E57200]"
            style={{ color: "#8A9BB5", fontFamily: "Plus Jakarta Sans, sans-serif" }}
          >
            <X size={16} /> Back to Atloria Live
          </button>
          <div className="block mb-6">
            <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full border-2"
              style={{ color: ORANGE, borderColor: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Last Updated: April 6, 2026
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-4"
            style={{ fontFamily: "Outfit, sans-serif" }}>
            Terms of Service
          </h1>
          <p className="text-base leading-relaxed max-w-2xl"
            style={{ color: "#C8CFDD", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            These Terms of Service ("Terms") constitute a binding legal agreement between you and Atloria Live ("Company," "we," "us"). By creating an account or using the Atloria Live mobile application, you agree to be bound by these Terms.
          </p>

          {/* Important notice */}
          <div className="mt-8 p-5 rounded-2xl border-2"
            style={{ background: `${ORANGE}18`, borderColor: ORANGE }}>
            <p className="text-sm font-bold leading-relaxed"
              style={{ color: ORANGE, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              IMPORTANT NOTICE: THESE TERMS CONTAIN A BINDING ARBITRATION PROVISION AND CLASS ACTION WAIVER (SECTION 7). IT AFFECTS YOUR LEGAL RIGHTS.
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
        <div className="flex flex-col gap-10">
          {sections.map((sec) => (
            <div key={sec.num} data-testid={`terms-section-${sec.num}`}
              className="p-8 rounded-2xl border-2"
              style={{ background: "#FFFFFF", borderColor: NAVY, boxShadow: `4px 4px 0px ${NAVY}` }}>
              <div className="flex items-start gap-4 mb-4">
                <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black text-white flex-shrink-0"
                  style={{ background: ORANGE, fontFamily: "Outfit, sans-serif" }}>
                  {sec.num}
                </span>
                <h2 className="text-xl font-bold pt-1" style={{ color: NAVY, fontFamily: "Outfit, sans-serif" }}>
                  {sec.title}
                </h2>
              </div>

              {sec.body && (
                <p className="text-sm leading-relaxed pl-13"
                  style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif", paddingLeft: "3.25rem" }}>
                  {sec.body}
                </p>
              )}

              {sec.subsections && (
                <div className="flex flex-col gap-5 mt-2" style={{ paddingLeft: "3.25rem" }}>
                  {sec.subsections.map((sub, j) => (
                    <div key={j}>
                      <h3 className="text-sm font-bold mb-2" style={{ color: NAVY, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                        {sub.title}
                      </h3>
                      {sub.body && (
                        <p className={`text-sm leading-relaxed ${sub.highlight ? "font-semibold" : ""}`}
                          style={{ color: sub.highlight ? NAVY : "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                          {sub.body}
                        </p>
                      )}
                      {sub.list && (
                        <ul className="flex flex-col gap-2 mt-2">
                          {sub.list.map((item, k) => (
                            <li key={k} className="flex items-start gap-2">
                              <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: ORANGE }} />
                              <span className="text-sm" style={{ color: "#556075", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                                <strong style={{ color: NAVY }}>{item.label}:</strong> {item.detail}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link to="/"
            data-testid="terms-back-home"
            className="inline-flex items-center gap-2 px-8 py-4 font-bold text-base rounded-full transition-all hover:-translate-y-1"
            style={{ background: ORANGE, color: "#FFFFFF", fontFamily: "Plus Jakarta Sans, sans-serif", boxShadow: `4px 4px 0px ${NAVY}` }}>
            Back to Atloria Live
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <div style={{ background: BG }}>
        <Navbar />
        <Routes>
          <Route path="/" element={<LandingPage autoOpenSurvey={window.location.hostname.startsWith("survey.")} />} />
          <Route path="/web" element={<LandingPage />} />
          <Route path="/survey" element={<LandingPage autoOpenSurvey={true} />} />
          <Route path="/terms" element={<TermsPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
